/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectorRef, Component, OnInit, ViewChild, inject, input, output } from '@angular/core';
import {
  LucideFlipHorizontal,
  LucideFlipVertical,
  LucideMinus,
  LucidePlus,
  LucideRotateCcw,
  LucideRotateCw,
  LucideX,
} from '@lucide/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { ImageCropperComponent, ImageCropperModule, ImageTransform } from 'ngx-image-cropper';
import { ImageCropDialogBase } from '../../components/image-crop-dialog/image-crop-dialog.base';
import { ImageCropResult, ImageCropperData } from '../../models/images/image-crop.model';
import { ButtonComponent } from '../../styleguide/components/buttons/default/button.component';
import { IconComponent } from '../../styleguide/components/icon/icon.component';
import { ToggleChipComponent } from '../../styleguide/components/toggle-chip/toggle-chip.component';
import { CropAspectPreset } from './v3-image-cropper.model';

const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const FREE_ASPECT_ID = 'free';

/**
 * The V3 crop editor: the cropper plus rotation, zoom, flips and aspect presets. It is a panel, not a
 * dialog, so it can be the second step inside the image dialog as well as the body of a crop-only
 * dialog. A caller that needs one exact frame (a cover banner, a round avatar) passes `aspectRatio` /
 * `cropperShape` as before; the presets are then hidden so the frame cannot be broken.
 */
@Component({
  selector: 'ptl-v3-image-cropper',
  templateUrl: './v3-image-cropper.component.html',
  styleUrls: ['./v3-image-cropper.component.scss'],
  standalone: true,
  imports: [TranslocoModule, ImageCropperModule, ButtonComponent, IconComponent, ToggleChipComponent],
})
export class V3ImageCropperComponent extends ImageCropDialogBase implements OnInit {
  data = input.required<ImageCropperData>();
  /** The crop-only dialog needs its own dismiss; inside the image dialog the shell owns that. */
  showClose = input(false);
  /** Overrides the cancel button's label — "Back" when cancelling returns to a previous step. */
  cancelLabel = input('');

  applied = output<ImageCropResult>();
  cancelled = output<void>();

  @ViewChild('cropper') cropper?: ImageCropperComponent;

  readonly LucideX = LucideX;
  readonly LucideRotateCcw = LucideRotateCcw;
  readonly LucideRotateCw = LucideRotateCw;
  readonly LucidePlus = LucidePlus;
  readonly LucideMinus = LucideMinus;
  readonly LucideFlipHorizontal = LucideFlipHorizontal;
  readonly LucideFlipVertical = LucideFlipVertical;

  transform: ImageTransform = { scale: 1, rotate: 0, flipH: false, flipV: false, translateUnit: 'px' };
  containWithinAspectRatio = false;
  onlyScaleDown = false;

  /** True when the caller fixed the frame, so the presets are not offered. */
  aspectLocked = false;
  aspectPresets: CropAspectPreset[] = [];
  activeAspectId = FREE_ASPECT_ID;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly transloco = inject(TranslocoService);

  get cancelText(): string {
    const label = this.cancelLabel();
    if (label) {
      return label;
    }
    return this.transloco.translate('translations.global.button.cancel');
  }

  protected get cropData(): ImageCropperData {
    return this.data();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.containWithinAspectRatio = this.data().containWithinAspectRatio ?? this.maintainAspectRatio;
    this.onlyScaleDown = this.data().onlyScaleDown ?? false;
    this.aspectLocked = this.maintainAspectRatio;
    this.aspectPresets = this.buildAspectPresets();
  }

  rotateLeft(): void {
    this.rotateBy(-90);
  }

  rotateRight(): void {
    this.rotateBy(90);
  }

  zoomIn(): void {
    this.zoomBy(ZOOM_STEP);
  }

  zoomOut(): void {
    this.zoomBy(-ZOOM_STEP);
  }

  flipHorizontal(): void {
    this.transform = { ...this.transform, flipH: !this.transform.flipH };
  }

  flipVertical(): void {
    this.transform = { ...this.transform, flipV: !this.transform.flipV };
  }

  selectAspect(preset: CropAspectPreset): void {
    this.activeAspectId = preset.id;

    if (preset.ratio === null) {
      this.maintainAspectRatio = false;
      return;
    }

    this.maintainAspectRatio = true;
    this.aspectRatio = preset.ratio;
  }

  resetEdits(): void {
    this.transform = { scale: 1, rotate: 0, flipH: false, flipV: false, translateUnit: 'px' };

    if (!this.aspectLocked) {
      this.activeAspectId = FREE_ASPECT_ID;
      this.maintainAspectRatio = false;
    }

    this.cropper?.resetCropperPosition();
  }

  protected closeWith(result?: ImageCropResult): void {
    if (result) {
      this.applied.emit(result);
      return;
    }
    this.cancelled.emit();
  }

  protected detectChanges(): void {
    this.cdr.detectChanges();
  }

  private rotateBy(degrees: number): void {
    const current = this.transform.rotate ?? 0;
    this.transform = { ...this.transform, rotate: current + degrees };
  }

  private zoomBy(step: number): void {
    const current = this.transform.scale ?? 1;
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current + step));
    this.transform = { ...this.transform, scale: Number(next.toFixed(2)) };
  }

  private buildAspectPresets(): CropAspectPreset[] {
    return [
      { id: FREE_ASPECT_ID, label: this.transloco.translate('translations.global.imageCrop.aspect.free'), ratio: null },
      { id: 'square', label: '1:1', ratio: 1 },
      { id: 'four-three', label: '4:3', ratio: 4 / 3 },
      { id: 'sixteen-nine', label: '16:9', ratio: 16 / 9 },
    ];
  }
}
